import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getAdminReferralEarnings } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import { DollarSign, Loader2 } from 'lucide-react';

export default function AdminReferralEarnings(): React.JSX.Element {
  const toast = useToast();
  const [earnings, setEarnings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadEarnings();
  }, [statusFilter]);

  const loadEarnings = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      const res = await getAdminReferralEarnings(params);
      setEarnings(res.earnings || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load earnings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const totalCommission = earnings.reduce((sum, e) => sum + parseFloat(e.commission_amount), 0);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <DollarSign className="w-7 h-7 text-emerald-600" />
          <h1 className="text-2xl font-bold text-slate-900">Referral Earnings</h1>
        </div>

        <Card className="mb-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Total Commission</p>
            <p className="text-2xl font-bold text-slate-900">₹{totalCommission.toLocaleString('en-IN')}</p>
          </div>
        </Card>

        <div className="flex gap-2 mb-4">
          {['', 'pending', 'approved', 'paid'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : earnings.length === 0 ? (
          <Card className="text-center py-12 text-slate-500">No earnings found.</Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">Referrer</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">Referred</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">Course</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">Purchase</th>
                  <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">Commission</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {earnings.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-slate-900">{e.referrer_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{e.referred_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{e.course_title || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">₹{Number(e.purchase_amount).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm font-bold text-emerald-600 text-right">₹{Number(e.commission_amount).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        e.status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                        e.status === 'approved' ? 'bg-amber-50 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {e.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
